// Document security API: purge (delete original) per-document and per-case.
//
// Purging removes the original file bytes from disk but keeps the metadata
// row for the audit trail. After purge, download/view is disabled.
import apiClient from './client';

export interface PurgeResult {
  document_id?: string;
  case_id?: string;
  purged: boolean;
  already_purged?: boolean;
  purged_count?: number;
}

/** Purge a single document's original file bytes (compliance/operator only). */
export async function purgeDocument(documentId: string): Promise<PurgeResult> {
  const { data } = await apiClient.delete<PurgeResult>(`/documents/${documentId}`);
  return data;
}

/** Purge ALL original documents for a case at once (compliance/operator only). */
export async function purgeCaseDocuments(caseId: string): Promise<PurgeResult> {
  const { data } = await apiClient.delete<PurgeResult>(
    `/cases/${caseId}/documents`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Per-document ingest control (operator 2026-10-27: cancel + reingest).
// Long-doc ingests are backgrounded; cancel stops an in-flight AI analyze
// job; reingest re-runs extraction + AI analysis on the stored original.
// ---------------------------------------------------------------------------

export interface IngestJobStatus {
  job_id: string | null;
  doc_id: string;
  case_id: string;
  status: string;
  cancel_requested?: boolean;
  processing_status?: string;
  chunk_count?: number;
  skip_ai?: boolean;
  error?: string | null;
  started_at?: number;
  updated_at?: number;
}

/** Cancel an in-flight backgrounded ingest (AI analysis) for a document. */
export async function cancelIngest(
  caseId: string,
  documentId: string
): Promise<IngestJobStatus> {
  const { data } = await apiClient.post<IngestJobStatus>(
    `/cases/${caseId}/documents/${documentId}/ingest/cancel`
  );
  return data;
}

/** Poll an in-flight ingest job for a document (optional job_id). */
export async function getIngestStatus(
  caseId: string,
  documentId: string,
  jobId?: string
): Promise<IngestJobStatus> {
  const params = jobId ? { params: { job_id: jobId } } : undefined;
  const { data } = await apiClient.get<IngestJobStatus>(
    `/cases/${caseId}/documents/${documentId}/ingest/status`,
    params
  );
  return data;
}

/** Re-run ingest (text extraction + AI analysis) on an existing stored file. */
export async function reingestDocument(
  caseId: string,
  documentId: string,
  opts?: { skip_ai?: boolean; skip_strip?: boolean }
): Promise<IngestJobStatus> {
  const form = new FormData();
  form.append('skip_ai', String(!!opts?.skip_ai));
  form.append('skip_strip', String(!!opts?.skip_strip));
  const { data } = await apiClient.post<IngestJobStatus>(
    `/cases/${caseId}/documents/${documentId}/reingest`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

// ---------------------------------------------------------------------------
// Per-file re-extract in place (operator 2026-10-28).
// Re-runs the extraction pipeline on the cached text of ONE private (intake)
// document + applies a FILL/PRESERVE/OVERWRITE/LEAVE policy per field
// (manual edits preserved; AI/det values overwritten; blanks filled).
// Backgrounded as a cancelable job. Only available on intake (private) docs.
// ---------------------------------------------------------------------------

export interface ReextractDocJobStatus {
  job_id: string;
  doc_id: string;
  case_id: string;
  status: string;
  cancel_requested?: boolean;
  error?: string | null;
  summary?: Record<string, unknown> | null;
  started_at?: number;
  updated_at?: number;
}

/** Start a per-file re-extract (backgrounded, cancelable). Returns 202. */
export async function reextractDocument(
  caseId: string,
  documentId: string
): Promise<ReextractDocJobStatus> {
  const { data } = await apiClient.post<ReextractDocJobStatus>(
    `/cases/${caseId}/documents/${documentId}/re-extract`
  );
  return data;
}

/** Cancel an in-flight per-file re-extract job. */
export async function cancelReextractDocument(
  caseId: string,
  documentId: string
): Promise<ReextractDocJobStatus> {
  const { data } = await apiClient.post<ReextractDocJobStatus>(
    `/cases/${caseId}/documents/${documentId}/re-extract/cancel`
  );
  return data;
}

/** Poll an in-flight per-file re-extract job (optional job_id). */
export async function getReextractDocumentStatus(
  caseId: string,
  documentId: string,
  jobId?: string
): Promise<ReextractDocJobStatus> {
  const params = jobId ? { params: { job_id: jobId } } : undefined;
  const { data } = await apiClient.get<ReextractDocJobStatus>(
    `/cases/${caseId}/documents/${documentId}/re-extract/status`,
    params
  );
  return data;
}

